/**
 * Dispute Round Detail Page
 * 
 * Shows complete details for a specific dispute round including:
 * - Round metadata and status
 * - Findings list
 * - Package list with letter status
 * - Status history
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { metro2Service, ROUND_STATUS_INFO, STRATEGY_MODE_INFO } from '../../services/metro2Service';
import { StatusTimeline, SimpleStatusTimeline } from '../../components/disputes/StatusTimeline';
import LetterPreviewModal from '../../components/disputes/LetterPreviewModal';
import { formatTimestamp, formatDateTimeShort } from '../../utils/dateTime';
import {
  ArrowLeft,
  FileText,
  Loader2,
  CheckCircle,
  Clock,
  AlertCircle,
  Download,
  Eye,
  RefreshCw,
  Building2,
  CreditCard,
  Search,
  ChevronRight,
  History,
  Maximize2,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';

// Bureau colors
const BUREAU_COLORS = {
  TransUnion: 'bg-emerald-100 text-emerald-700',
  Experian: 'bg-amber-100 text-amber-700',
  Equifax: 'bg-red-100 text-red-700',
};

const RoundDetailPage = () => {
  const { roundId } = useParams();
  const navigate = useNavigate();
  const { token, user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [round, setRound] = useState(null);
  const [findings, setFindings] = useState([]);
  const [packages, setPackages] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [letterContent, setLetterContent] = useState(null);
  const [letterLoading, setLetterLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [showFullPreview, setShowFullPreview] = useState(false);
  const [currentPackageIndex, setCurrentPackageIndex] = useState(0);
  const [generatingLetters, setGeneratingLetters] = useState(false);
  
  // Determine user tier for simplified vs detailed status view
  const userTier = user?.subscription_tier || user?.tier || 'auto_starter';
  const isAutoStarter = userTier === 'auto_starter' || userTier === 'self_repair_customer';
  
  useEffect(() => {
    if (roundId) {
      loadRoundData();
    }
  }, [roundId]);
  
  const loadRoundData = async () => {
    setLoading(true);
    try {
      const result = await metro2Service.getRound(roundId, token);
      setRound(result.round);
      setFindings(result.findings || []);
      setPackages(result.packages || []);
    } catch (error) {
      console.error('Failed to load round:', error);
      toast.error('Failed to load round details');
    } finally {
      setLoading(false);
    }
  };
  
  const handleViewLetter = async (pkg, openModal = false) => {
    setSelectedPackage(pkg);
    
    // Find package index
    const pkgIndex = packages.findIndex(p => p.id === pkg.id);
    if (pkgIndex >= 0) {
      setCurrentPackageIndex(pkgIndex);
    }
    
    // If package has no letter_url, it's in pending state - don't attempt fetch
    if (!pkg.letter_url) {
      setLetterContent(null);
      // Open modal if requested (will show pending state)
      if (openModal) {
        setShowFullPreview(true);
      }
      return;
    }
    
    setLetterLoading(true);
    
    try {
      // Check if letter_url is a data URL (inline base64 content)
      if (pkg.letter_url.startsWith('data:text/html;base64,')) {
        // Decode base64 content directly
        const base64Content = pkg.letter_url.replace('data:text/html;base64,', '');
        const htmlContent = atob(base64Content);
        setLetterContent({
          success: true,
          package_id: pkg.id,
          round_id: roundId,
          letter_url: pkg.letter_url,
          letter_status: 'ready',
          letter_generated: true,
          content: htmlContent,
        });
        
        // Open modal if requested
        if (openModal) {
          setShowFullPreview(true);
        }
        return;
      }
      
      // Otherwise fetch from the API
      const result = await metro2Service.getLetter(roundId, pkg.id, token);
      setLetterContent(result);
      
      // Open modal if requested
      if (openModal) {
        setShowFullPreview(true);
      }
    } catch (error) {
      console.error('Failed to load letter:', error);
      // Only show error toast for actual failures, not for "not generated yet"
      const errorMessage = error.message || '';
      if (errorMessage.includes('not yet generated') || errorMessage.includes('not generated')) {
        // This is a pending state, not a failure - don't show error toast
        setLetterContent(null);
      } else {
        toast.error('Failed to load letter');
        setLetterContent(null);
      }
    } finally {
      setLetterLoading(false);
    }
  };
  
  const handlePackageChange = async (index) => {
    if (index >= 0 && index < packages.length) {
      setCurrentPackageIndex(index);
      await handleViewLetter(packages[index], false);
    }
  };
  
  const handleDownloadLetter = (pkg) => {
    if (!letterContent?.content) {
      toast.error('Letter content not available');
      return;
    }
    
    const blob = new Blob([letterContent.content], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `dispute_letter_${pkg.target_bureau || 'package'}_${roundId.slice(0, 8)}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Letter downloaded');
  };
  
  // Handle letter generation for packages without letters
  const handleGenerateLetters = async () => {
    setGeneratingLetters(true);
    try {
      const result = await metro2Service.renderLetters(roundId, 'standard', user?.id, token);
      
      if (result.success) {
        toast.success(`Generated ${result.letters_rendered} letters successfully`);
        // Reload round data to get updated packages with letter_url
        await loadRoundData();
        
        // If a package was selected, reload its letter content
        if (selectedPackage) {
          const updatedPkg = packages.find(p => p.id === selectedPackage.id);
          if (updatedPkg?.letter_url) {
            await handleViewLetter(updatedPkg);
          }
        }
      } else {
        toast.error(result.message || 'Failed to generate letters');
      }
    } catch (error) {
      console.error('Failed to generate letters:', error);
      toast.error(error.message || 'Failed to generate letters');
    } finally {
      setGeneratingLetters(false);
    }
  };
  
  // Check if any packages need letter generation
  const packagesNeedGeneration = packages.some(pkg => !pkg.letter_url);
  const allPackagesHaveLetters = packages.length > 0 && packages.every(pkg => pkg.letter_url);
  
  // Derive effective status - if round status is 'generated' but all packages have letters, 
  // override display to show 'letters_generated'
  const effectiveStatus = (round?.status === 'generated' && allPackagesHaveLetters) 
    ? 'letters_generated' 
    : round?.status;
  
  const statusInfo = round ? ROUND_STATUS_INFO[effectiveStatus] || { label: effectiveStatus, color: 'bg-stone-100 text-stone-700' } : {};
  const strategyInfo = round ? STRATEGY_MODE_INFO[round.strategy_mode] : null;
  
  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }
  
  if (!round) {
    return (
      <div className="min-h-screen bg-stone-50 flex flex-col items-center justify-center">
        <AlertCircle size={48} className="text-stone-400 mb-4" />
        <h2 className="text-xl font-semibold text-stone-900 mb-2">Round Not Found</h2>
        <p className="text-stone-600 mb-4">The dispute round you're looking for doesn't exist.</p>
        <Button onClick={() => navigate('/disputes')}>
          <ArrowLeft size={18} className="mr-2" />
          Back to Disputes
        </Button>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-stone-50" data-testid="round-detail-page">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-6xl mx-auto px-6 py-6">
          <Button variant="ghost" className="mb-4" onClick={() => navigate('/disputes')}>
            <ArrowLeft size={18} className="mr-2" />
            Back to Disputes
          </Button>
          
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-2xl font-bold text-stone-900">
                  Dispute Round
                </h1>
                <Badge className={statusInfo.color}>{statusInfo.label}</Badge>
              </div>
              <p className="text-stone-500 font-mono text-sm">{round.id}</p>
            </div>
            
            <Button variant="outline" onClick={loadRoundData}>
              <RefreshCw size={18} className="mr-2" />
              Refresh
            </Button>
          </div>
        </div>
      </div>
      
      {/* Content */}
      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="status">
              <History size={16} className="mr-1" />
              Status Tracking
            </TabsTrigger>
            <TabsTrigger value="findings">Findings ({findings.length})</TabsTrigger>
            <TabsTrigger value="packages">Packages ({packages.length})</TabsTrigger>
          </TabsList>
        </Tabs>
        
        {/* Overview Tab */}
        {activeTab === 'overview' && (
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Left: Round Info + Findings */}
          <div className="lg:col-span-2 space-y-6">
            {/* Round Metadata */}
            <div className="bg-white border rounded-xl p-6" data-testid="round-metadata">
              <h2 className="font-semibold text-stone-900 mb-4">Round Details</h2>
              
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-stone-500 uppercase tracking-wide">Strategy</p>
                  <p className="font-medium text-stone-900 flex items-center gap-2">
                    <Building2 size={16} className="text-stone-400" />
                    {strategyInfo?.name || round.strategy_mode}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-stone-500 uppercase tracking-wide">Tier</p>
                  <p className="font-medium text-stone-900 capitalize">{round.tier?.replace('_', ' ')}</p>
                </div>
                <div>
                  <p className="text-xs text-stone-500 uppercase tracking-wide">Created</p>
                  <p className="font-medium text-stone-900">
                    {formatTimestamp(round.created_at)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-stone-500 uppercase tracking-wide">Round Number</p>
                  <p className="font-medium text-stone-900">#{round.round_number || 1}</p>
                </div>
                <div>
                  <p className="text-xs text-stone-500 uppercase tracking-wide">Accounts</p>
                  <p className="font-medium text-stone-900 flex items-center gap-2">
                    <CreditCard size={16} className="text-stone-400" />
                    {round.account_snapshots?.length || 0}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-stone-500 uppercase tracking-wide">Inquiries</p>
                  <p className="font-medium text-stone-900 flex items-center gap-2">
                    <Search size={16} className="text-stone-400" />
                    {round.inquiry_snapshots?.length || 0}
                  </p>
                </div>
              </div>
            </div>
            
            {/* Findings */}
            <div className="bg-white border rounded-xl p-6" data-testid="round-findings">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-semibold text-stone-900">
                  Findings ({findings.length})
                </h2>
              </div>
              
              {findings.length === 0 ? (
                <div className="text-center py-8 text-stone-500">
                  <AlertCircle size={32} className="mx-auto mb-2 opacity-50" />
                  <p>No findings generated yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {findings.map(finding => (
                    <div
                      key={finding.id}
                      className="border rounded-lg p-4 hover:bg-stone-50 transition-colors"
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <FileText size={16} className="text-stone-400" />
                          <span className="font-medium text-stone-900">{finding.rule_name}</span>
                          {finding.bureau && (
                            <Badge className={BUREAU_COLORS[finding.bureau]}>{finding.bureau}</Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-xs">
                            {Math.round(finding.confidence_score * 100)}% confidence
                          </Badge>
                        </div>
                      </div>
                      <p className="text-sm text-stone-600">{finding.description}</p>
                      {finding.metro_field_codes?.length > 0 && (
                        <div className="flex gap-1 mt-2">
                          {finding.metro_field_codes.map(code => (
                            <Badge key={code} variant="outline" className="text-xs font-mono">
                              {code}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          
          {/* Right: Packages + Letter Preview */}
          <div className="space-y-6">
            {/* Packages */}
            <div className="bg-white border rounded-xl p-6" data-testid="round-packages">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-semibold text-stone-900">
                  Packages ({packages.length})
                </h2>
                {packagesNeedGeneration && packages.length > 0 && (
                  <Button 
                    size="sm"
                    onClick={handleGenerateLetters}
                    disabled={generatingLetters}
                    className="bg-emerald-600 hover:bg-emerald-700"
                    data-testid="generate-letters-btn"
                  >
                    {generatingLetters ? (
                      <>
                        <Loader2 size={14} className="mr-1 animate-spin" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <FileText size={14} className="mr-1" />
                        Generate Letters
                      </>
                    )}
                  </Button>
                )}
              </div>
              
              {packages.length === 0 ? (
                <div className="text-center py-8 text-stone-500">
                  <FileText size={32} className="mx-auto mb-2 opacity-50" />
                  <p>No packages yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {packages.map(pkg => (
                    <div
                      key={pkg.id}
                      className={`border rounded-lg p-4 cursor-pointer transition-all ${
                        selectedPackage?.id === pkg.id
                          ? 'border-emerald-500 bg-emerald-50'
                          : 'hover:border-stone-300'
                      }`}
                      onClick={() => handleViewLetter(pkg)}
                    >
                      <div className="flex items-center justify-between mb-2">
                        {pkg.target_bureau ? (
                          <Badge className={BUREAU_COLORS[pkg.target_bureau]}>{pkg.target_bureau}</Badge>
                        ) : (
                          <span className="font-medium text-stone-700">{pkg.package_type}</span>
                        )}
                        {generatingLetters ? (
                          <Badge className="bg-amber-100 text-amber-700">
                            <Loader2 size={12} className="mr-1 animate-spin" />
                            Generating
                          </Badge>
                        ) : pkg.letter_url ? (
                          <Badge className="bg-emerald-100 text-emerald-700">
                            <CheckCircle size={12} className="mr-1" />
                            Ready
                          </Badge>
                        ) : (
                          <Badge className="bg-stone-100 text-stone-600">
                            <Clock size={12} className="mr-1" />
                            Pending
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-stone-600 mb-2">
                        {pkg.findings_snapshot?.length || 0} findings
                      </p>
                      
                      {/* Actions - only show when letter exists */}
                      {pkg.letter_url && (
                        <div className="flex gap-2 mt-3 pt-3 border-t">
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="flex-1"
                            onClick={(e) => { e.stopPropagation(); handleViewLetter(pkg, true); }}
                          >
                            <Maximize2 size={14} className="mr-1" />
                            Full Preview
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={(e) => { e.stopPropagation(); handleDownloadLetter(pkg); }}
                          >
                            <Download size={14} />
                          </Button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {/* Letter Preview */}
            {selectedPackage && (
              <div className="bg-white border rounded-xl p-6" data-testid="letter-preview">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-semibold text-stone-900">Letter Preview</h2>
                  <div className="flex gap-2">
                    {letterContent?.content && (
                      <>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => setShowFullPreview(true)}
                          className="text-emerald-600 hover:text-emerald-700"
                        >
                          <Maximize2 size={14} className="mr-1" />
                          Full Screen
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleDownloadLetter(selectedPackage)}
                        >
                          <Download size={14} className="mr-1" />
                          Download
                        </Button>
                      </>
                    )}
                  </div>
                </div>
                
                {/* State: Loading */}
                {letterLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-6 h-6 animate-spin text-stone-400" />
                    <span className="ml-2 text-stone-500">Loading preview...</span>
                  </div>
                ) : generatingLetters ? (
                  /* State: Generating */
                  <div className="text-center py-12 text-stone-500">
                    <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-amber-100 flex items-center justify-center">
                      <Loader2 size={24} className="animate-spin text-amber-600" />
                    </div>
                    <p className="font-medium text-stone-700 mb-1">Generating Letter...</p>
                    <p className="text-sm text-stone-400">
                      Your dispute letter is being created
                    </p>
                  </div>
                ) : letterContent?.content ? (
                  /* State: Ready - show content */
                  <div 
                    className="border rounded-lg p-4 max-h-[400px] overflow-y-auto prose prose-sm"
                    dangerouslySetInnerHTML={{ __html: letterContent.content }}
                  />
                ) : (
                  /* State: Empty - PASSIVE, no CTA */
                  <div className="text-center py-12 text-stone-400">
                    <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-stone-100 flex items-center justify-center">
                      <FileText size={24} className="text-stone-400" />
                    </div>
                    <p className="font-medium text-stone-600 mb-1">No letter generated yet</p>
                    <p className="text-sm">
                      Use the <span className="font-medium text-emerald-600">Generate Letters</span> button above to create dispute letters for all packages
                    </p>
                  </div>
                )}
              </div>
            )}
            
            {/* Quick Link to Package Detail */}
            {selectedPackage && (
              <Link
                to={`/disputes/${roundId}/packages/${selectedPackage.id}`}
                className="block text-center text-emerald-600 hover:text-emerald-700 text-sm"
              >
                View Full Package Details
                <ChevronRight size={16} className="inline ml-1" />
              </Link>
            )}
          </div>
        </div>
        )}
        
        {/* Status Tracking Tab */}
        {activeTab === 'status' && (
          <div className="bg-white border rounded-xl p-6" data-testid="status-tracking-tab">
            <h2 className="font-semibold text-stone-900 mb-4 flex items-center gap-2">
              <History size={20} />
              Dispute Status Tracking
            </h2>
            <p className="text-sm text-stone-600 mb-6">
              Track the lifecycle of this dispute from creation through resolution.
            </p>
            
            {/* Use simplified view for Auto Starter, full view for Pro */}
            {isAutoStarter ? (
              <SimpleStatusTimeline roundId={roundId} />
            ) : (
              <StatusTimeline 
                roundId={roundId}
                onStatusChange={() => loadRoundData()}
              />
            )}
          </div>
        )}
        
        {/* Findings Tab */}
        {activeTab === 'findings' && (
          <div className="bg-white border rounded-xl p-6" data-testid="findings-tab">
            <h2 className="font-semibold text-stone-900 mb-4">
              Findings ({findings.length})
            </h2>
            
            {findings.length === 0 ? (
              <div className="text-center py-8 text-stone-500">
                <AlertCircle size={32} className="mx-auto mb-2 opacity-50" />
                <p>No findings generated yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {findings.map(finding => (
                  <div
                    key={finding.id}
                    className="border rounded-lg p-4 hover:bg-stone-50 transition-colors"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <FileText size={16} className="text-stone-400" />
                        <span className="font-medium text-stone-900">{finding.rule_name}</span>
                        {finding.bureau && (
                          <Badge className={BUREAU_COLORS[finding.bureau]}>{finding.bureau}</Badge>
                        )}
                      </div>
                      <Badge variant="outline" className="text-xs">
                        {Math.round(finding.confidence_score * 100)}% confidence
                      </Badge>
                    </div>
                    <p className="text-sm text-stone-600">{finding.description}</p>
                    {finding.metro_field_codes?.length > 0 && (
                      <div className="flex gap-1 mt-2">
                        {finding.metro_field_codes.map(code => (
                          <Badge key={code} variant="outline" className="text-xs font-mono">
                            {code}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        
        {/* Packages Tab */}
        {activeTab === 'packages' && (
          <div className="grid lg:grid-cols-2 gap-6">
            <div className="bg-white border rounded-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-semibold text-stone-900">
                  Packages ({packages.length})
                </h2>
                {packagesNeedGeneration && packages.length > 0 && (
                  <Button 
                    size="sm"
                    onClick={handleGenerateLetters}
                    disabled={generatingLetters}
                    className="bg-emerald-600 hover:bg-emerald-700"
                  >
                    {generatingLetters ? (
                      <>
                        <Loader2 size={14} className="mr-1 animate-spin" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <FileText size={14} className="mr-1" />
                        Generate Letters
                      </>
                    )}
                  </Button>
                )}
              </div>
              
              {packages.length === 0 ? (
                <div className="text-center py-8 text-stone-500">
                  <FileText size={32} className="mx-auto mb-2 opacity-50" />
                  <p>No packages yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {packages.map(pkg => (
                    <div
                      key={pkg.id}
                      className={`border rounded-lg p-4 cursor-pointer transition-all ${
                        selectedPackage?.id === pkg.id
                          ? 'border-emerald-500 bg-emerald-50'
                          : 'hover:border-stone-300'
                      }`}
                      onClick={() => handleViewLetter(pkg)}
                    >
                      <div className="flex items-center justify-between mb-2">
                        {pkg.target_bureau ? (
                          <Badge className={BUREAU_COLORS[pkg.target_bureau]}>{pkg.target_bureau}</Badge>
                        ) : (
                          <span className="font-medium text-stone-700">{pkg.package_type}</span>
                        )}
                        {generatingLetters ? (
                          <Badge className="bg-amber-100 text-amber-700">
                            <Loader2 size={12} className="mr-1 animate-spin" />
                            Generating
                          </Badge>
                        ) : pkg.letter_url ? (
                          <Badge className="bg-emerald-100 text-emerald-700">
                            <CheckCircle size={12} className="mr-1" />
                            Ready
                          </Badge>
                        ) : (
                          <Badge className="bg-stone-100 text-stone-600">
                            <Clock size={12} className="mr-1" />
                            Pending
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-stone-600">
                        {pkg.findings_snapshot?.length || 0} findings
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {/* Letter Preview in packages tab */}
            <div className="bg-white border rounded-xl p-6">
              <h2 className="font-semibold text-stone-900 mb-4">Letter Preview</h2>
              
              {selectedPackage ? (
                <>
                  {letterLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="w-6 h-6 animate-spin text-stone-400" />
                      <span className="ml-2 text-stone-500">Loading letter...</span>
                    </div>
                  ) : generatingLetters ? (
                    <div className="text-center py-12 text-stone-500">
                      <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-amber-100 flex items-center justify-center">
                        <Loader2 size={24} className="animate-spin text-amber-600" />
                      </div>
                      <p className="font-medium text-stone-700 mb-1">Generating Letter...</p>
                      <p className="text-sm text-stone-400">
                        Your dispute letter is being created
                      </p>
                    </div>
                  ) : letterContent?.content ? (
                    <>
                      <div className="flex justify-end mb-4">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleDownloadLetter(selectedPackage)}
                        >
                          <Download size={14} className="mr-1" />
                          Download
                        </Button>
                      </div>
                      <div 
                        className="border rounded-lg p-4 max-h-[500px] overflow-y-auto prose prose-sm"
                        dangerouslySetInnerHTML={{ __html: letterContent.content }}
                      />
                    </>
                  ) : (
                    <div className="text-center py-12 text-stone-400">
                      <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-stone-100 flex items-center justify-center">
                        <FileText size={24} className="text-stone-400" />
                      </div>
                      <p className="font-medium text-stone-600 mb-1">No letter generated yet</p>
                      <p className="text-sm">
                        Use the <span className="font-medium text-emerald-600">Generate Letters</span> button above to create dispute letters
                      </p>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-12 text-stone-500">
                  <FileText size={32} className="mx-auto mb-2 opacity-50" />
                  <p>Select a package to preview</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      
      {/* Full-screen Letter Preview Modal */}
      <LetterPreviewModal
        isOpen={showFullPreview}
        onClose={() => setShowFullPreview(false)}
        letterContent={letterContent?.content}
        packageInfo={selectedPackage}
        packages={packages}
        currentPackageIndex={currentPackageIndex}
        onPackageChange={handlePackageChange}
        onDownload={() => selectedPackage && handleDownloadLetter(selectedPackage)}
        onGenerate={null}
        isLoading={letterLoading}
        isGenerating={generatingLetters}
      />
    </div>
  );
};

export default RoundDetailPage;
